<template>
  <li
    ref="root"
    class="relative snap-end rounded-8 w-full mx-1 flex flex-col"
    data-test="call-session-user-card-root"
    :title="cardTitle"
    :class="{
      'bg-charleston-green border-charleston-green': !light,
      'bg-surf-bg-3 border-transparent': light,
      'outline-2 outline-satel-blue': isSelected,
      'opacity-40!': memberStatus === MemberStatus.Dialing,
      'border-4': !hideBorder,
      'mx-0!': light,
    }"
    @click="onClick"
  >
    <header class="absolute z-9 top-0 flex flex-row flex-1 justify-between w-full">
      <slot
        name="indicator"
        :status-icon="statusIcon"
      />
      <slot
        v-if="!currentGuest || isCurrentUser"
        name="action"
        class="z-10"
        :member-status="memberStatus"
      />
    </header>
    <main
      ref="container"
      v-drag-scroll="scale > 1 && isStreamMediaActive"
      class="h-full w-full relative overflow-hidden rounded-8"
      :class="{
        'cursor-pointer': scale > 1,
      }"
      @mouseenter="onMouseEnter"
      @mouseleave="isMouseOver = false"
    >
      <template v-if="isStreamMediaActive">
        <div
          class="rounded-8 absolute top-0 left-0 flex items-center justify-center"
          :style="videoScaleStyle"
        >
          <video
            ref="video"
            class="rounded-8"
            :class="videoFitClass"
            :style="screenShareAspectStyle"
            :srcObject="playbackStream"
            :autoplay="true"
            @loadeddata="markVideoLoaded"
            @canplay="markVideoLoaded"
            @loadedmetadata="syncVideoLoadedState"
            @resize="syncVideoAspectRatio"
          />
          <div
            v-if="!isVideoLoaded"
            class="absolute inset-0 z-5 flex flex-col items-center justify-center gap-8 pointer-events-none rounded-8"
            data-test="call-session-user-card-video-loader"
          >
            <logo-animated
              :class="isBig ? 'w-[96px] h-[96px]' : 'w-[48px] h-[48px]'"
              class="relative -top-[0.6rem]"
            />
            <slot
              name="avatar-footer"
              :contact-name="contact.name"
            />
          </div>
        </div>
        <video-stream-stats-badge
          v-if="sessionId && videoTrackId"
          :session-id="sessionId"
          :variant="isCurrentUser ? 'outbound' : 'inbound'"
          :track-id="videoTrackId"
          :compact="!isBig"
        />
      </template>
      <template v-else>
        <div class="absolute top-1/2 -translate-y-1/2 left-1/2 transform -translate-x-1/2  w-full h-full flex flex-col items-center justify-center gap-8">
          <contact-avatar
            :size="isBig ? 160 : 48"
            :contact="contact"
            :contact-type="contact.type"
            hide-border
            class="border border-white relative -top-[0.6rem] avatar-card--compact aspect-square! w-auto!"
          />
          <slot
            name="avatar-footer"
            :contact-name="contact.name"
          />
        </div>
        <audio
          v-if="audioOnlyPlaybackStream"
          ref="audioOnly"
          class="hidden"
          :srcObject="audioOnlyPlaybackStream"
          autoplay
        />
      </template>
    </main>
    <slot
      name="footer"
      :contact-name="contact.name"
      :video-active="videoActive"
      :title="contact.userAgent"
    />
    <scale-button
      v-if="scale > 1"
      class="absolute left-6 bottom-8 z-20"
      :scale="scale"
      @click="setDefault"
    />
  </li>
</template>

<script setup lang="ts">
import { IconName } from '@wui/common-library'
import { PServed } from '@wui/im'
import { computed, inject, toRefs, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'


import { callSessionsLayoutsStore, Layouts, useCallSessionLayout } from '@/entities/call-session-layout'
import {
  buildParticipantPlaybackStream,
  getParticipantAudioTrack,
  shouldAttachAudioToVideoTrack,
  useParticipantMediaPlayback,
  VideoStreamStatsBadge,
} from '@/entities/call-session-media'
import { useStoreConferenceMediaStatuses, ConferenceParticipant } from '@/entities/conference-participant'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useIntercomRoomParticipantsStatusesStore, MemberStatus } from '@/entities/room'
import { toggleLayoutKey } from '@/entities/use-call-session'
import { RTCSessionFacade, getIsStreamIsScreenSharing } from '@/entities/webrtc'

import { LogoAnimated } from '@/shared/components/logo'
import { ScaleButton } from '@/shared/components/scale'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useLocalization } from '@/shared/composables/localization'
import { useWheelScale } from '@/shared/composables/wheel-scale'
import { vDragScroll } from '@/shared/directives/v-drag-scroll'
import { fromPServedUser } from '@/shared/utils/uri'

const props = defineProps<{
  contact: ConferenceParticipant
  selectedContactId?: string
  roomPServed: PServed
  session?: RTCSessionFacade
  canScaleVideo?: boolean
  hideBorder?: boolean
  isBig?: boolean
  stream?: MediaStream
  selectedByStream?: boolean
  light?: boolean
  /** When false (screen-share tile), do not play participant audio on this card. Default true. */
  playsParticipantAudio?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', contactId: string, stream?: MediaStream): void
}>()

const { session, contact, roomPServed, selectedContactId, stream } = toRefs(props)
const { t } = useLocalization()

const cardTitle = computed(() => {
  return [contact.value.userAgent, t('keys.leftRight')].filter(Boolean).join('\n')
})

const sessionId = computed(() => session.value?.sessionId)
const videoTrackId = computed(() => stream.value?.getVideoTracks().at(0)?.id)

const { getParticipantMediaStatus, getIsLocallyMutedAudio } = useStoreConferenceMediaStatuses()
const { currentUser } = useCurrentUser()
const { currentGuest } = useGuestAuth()
const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()
const { setLayout } = useCallSessionLayout()
const callSessionTileSelect = inject(toggleLayoutKey, null)

const video = ref<HTMLVideoElement>()
const audioOnly = ref<HTMLAudioElement>()
const root = ref<HTMLElement>()
const container = ref<HTMLElement>()
const videoContainerWidth = ref(0)
const videoContainerHeight = ref(0)
const videoSelectorIsShowed = ref(false)
const isMouseOver = ref(false)
const isVideoLoaded = ref(false)
const videoAspectRatio = ref<string>()
const activePlaybackElement = ref<HTMLMediaElement>()

const syncVideoBaseSize = () => {
  const el = container.value
  if (!el) {
    return
  }
  videoContainerWidth.value = el.clientWidth
  videoContainerHeight.value = el.clientHeight
}

const markVideoLoaded = () => {
  if (!stream.value) {
    return
  }
  isVideoLoaded.value = true
  syncVideoAspectRatio()
}

const syncVideoAspectRatio = () => {
  const el = video.value
  if (el?.videoWidth && el?.videoHeight) {
    videoAspectRatio.value = `${el.videoWidth} / ${el.videoHeight}`
    return
  }

  const { width, height } = stream.value?.getVideoTracks()[0]?.getSettings() ?? {}
  if (width && height) {
    videoAspectRatio.value = `${width} / ${height}`
    return
  }

  videoAspectRatio.value = undefined
}

/** Remount (layout switch): events may not re-fire if stream already has frames. */
const syncVideoLoadedState = () => {
  const el = video.value
  if (!el || !stream.value) {
    return
  }
  if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    isVideoLoaded.value = true
  }
  syncVideoAspectRatio()
}

const { scale, init, destroy, setDefault } = useWheelScale(container)

/** At scale 1 always fill container (avoids stuck small px after race). Pixels only when zoomed. */
const videoScaleStyle = computed(() => {
  if (!props.canScaleVideo || scale.value <= 1) {
    return { width: '100%', height: '100%' }
  }

  return {
    width: videoContainerWidth.value ? `${videoContainerWidth.value * scale.value}px` : '100%',
    height: videoContainerHeight.value ? `${videoContainerHeight.value * scale.value}px` : '100%',
  }
})

const statusLookupId = computed(() => {
  const internal = contact.value.internalNumber
  if (internal) return internal
  const pServed = contact.value.participantPServed ?? ''
  const normalized = fromPServedUser(pServed)
  return normalized || pServed
})
const memberStatus = computed(() => getParticipantStatus(roomPServed.value, statusLookupId.value, contact.value.callId))

const isCurrentUser = computed(() => {
  if (currentGuest.value) {
    return Boolean(contact.value.imLogin && currentGuest.value.pServed === contact.value.imLogin)
  }

  return Boolean(currentUser.value && contact.value.imLogin && currentUser.value.imLogin === contact.value.imLogin)
})

const memberMediaState = computed(() => getParticipantMediaStatus(contact.value.callId))

const isAudioMutedLocally = computed(() => isCurrentUser.value ? getIsLocallyMutedAudio(session.value?.callId.value ?? '') : false)

const isOnHold = computed(() => {
  return memberStatus.value === MemberStatus.OnHold
})

const videoActive = computed(() => {
  if (!session.value) {
    return false
  }
  if (!memberMediaState.value || memberMediaState.value.isVideoMuted || memberStatus.value === 'Dialing') {
    return false
  }
  return true
})

const isScreenSharingActive = computed(() => {
  if (!memberMediaState.value || memberMediaState.value.isScreenSharingMuted || memberStatus.value === 'Dialing') {
    return false
  }
  return true
})

const isLocalScreenStream = computed(() => {
  if (!stream.value) {
    return false
  }
  return session.value?.localScreenStream.value?.id === stream.value.id
})

/** Per-stream: participant may share screen while this card still shows their webcam. */
const isThisStreamScreenSharing = computed(() => {
  if (!stream.value) {
    return false
  }
  // Local getDisplayMedia tracks have browser UUIDs — no "screen" in track.id
  if (isLocalScreenStream.value) {
    return true
  }
  return getIsStreamIsScreenSharing(stream.value)
})

const videoFitClass = computed(() => {
  if (isThisStreamScreenSharing.value) {
    // CallLarge zoom scales the wrapper; video must fill it or wheel-zoom is invisible.
    if (props.canScaleVideo) {
      return 'w-full h-full object-contain'
    }
    return 'max-w-full max-h-full min-w-0 min-h-0 w-auto h-auto object-contain'
  }
  return 'w-full h-full object-cover'
})

const screenShareAspectStyle = computed(() => {
  if (!isThisStreamScreenSharing.value || !videoAspectRatio.value) {
    return undefined
  }
  return { aspectRatio: videoAspectRatio.value }
})

const isStreamMediaActive = computed(() => {
  if (!stream.value) {
    return false
  }
  // Local preview: stream ref exists ⇒ sharing is on (don't wait for IM mute flag)
  if (isLocalScreenStream.value) {
    return true
  }
  if (isThisStreamScreenSharing.value) {
    return isScreenSharingActive.value
  }
  return videoActive.value
})

const participantAudioTrack = computed(() => {
  if (!sessionId.value || !contact.value.shortCallId) {
    return undefined
  }
  return getParticipantAudioTrack(sessionId.value, contact.value.shortCallId)
})

const playsParticipantAudio = computed(() => props.playsParticipantAudio !== false)

const shouldPlayParticipantAudio = computed(() => {
  if (!playsParticipantAudio.value || isCurrentUser.value) {
    return false
  }
  if (isThisStreamScreenSharing.value) {
    return false
  }
  const videoTrack = stream.value?.getVideoTracks().at(0)
  if (videoTrack && !shouldAttachAudioToVideoTrack(videoTrack)) {
    return false
  }
  return true
})

const playbackStream = computed(() => {
  if (!stream.value) {
    return undefined
  }
  const videoTrack = stream.value.getVideoTracks().at(0)
  if (!shouldPlayParticipantAudio.value) {
    return buildParticipantPlaybackStream({ videoTrack }) ?? stream.value
  }
  return buildParticipantPlaybackStream({
    videoTrack,
    audioTrack: participantAudioTrack.value,
  }) ?? stream.value
})

const audioOnlyPlaybackStream = computed(() => {
  if (isStreamMediaActive.value || isCurrentUser.value || !playsParticipantAudio.value) {
    return undefined
  }
  return buildParticipantPlaybackStream({ audioTrack: participantAudioTrack.value })
})

const isPlaybackForceMuted = computed(() => {
  if (isCurrentUser.value || !shouldPlayParticipantAudio.value) {
    return true
  }
  if (isOnHold.value) {
    return true
  }
  if (memberMediaState.value?.isAudioMuted || isAudioMutedLocally.value) {
    return true
  }
  return false
})

const isAudioOnlyForceMuted = computed(() => {
  if (isOnHold.value || memberMediaState.value?.isAudioMuted) {
    return true
  }
  return false
})

watch([video, audioOnly, isStreamMediaActive, audioOnlyPlaybackStream], () => {
  if (isStreamMediaActive.value && video.value) {
    activePlaybackElement.value = video.value
    return
  }
  if (audioOnlyPlaybackStream.value && audioOnly.value) {
    activePlaybackElement.value = audioOnly.value
    return
  }
  activePlaybackElement.value = undefined
}, { flush: 'post' })

const playbackForceMuted = computed(() => {
  if (activePlaybackElement.value === audioOnly.value) {
    return isAudioOnlyForceMuted.value
  }
  return isPlaybackForceMuted.value
})

useParticipantMediaPlayback(activePlaybackElement, {
  forceMuted: playbackForceMuted,
})

const statusIcon = computed<{ mic: IconName, hand?: IconName | null }>(() => {
  let mic: IconName = (!memberMediaState.value || memberMediaState.value?.isAudioMuted || isAudioMutedLocally.value) ? 'micOffM' : 'micM'
  if (isOnHold.value) {
    mic = 'pauseM'
  } else {

  } if (memberStatus.value === 'Dialing') {
    mic = 'spinner'
  }

  return {
    mic,
    hand: memberMediaState.value?.handEnabled ? 'frontHand' : null,
  }
})

const onMouseEnter = () => {
  if (!isMouseOver.value) {
    videoSelectorIsShowed.value = true
  }

  isMouseOver.value = true
}

const resizeObserver = new ResizeObserver(() => {
  syncVideoBaseSize()
})

onMounted(() => {
  if (container.value) {
    resizeObserver.observe(container.value)
    syncVideoBaseSize()
    if (props.canScaleVideo) {
      init(container.value)
    }
  }
})

onUnmounted(() => {
  if (container.value && props.canScaleVideo) {
    destroy(container.value)
  }

  if (container.value) {
    resizeObserver.unobserve(container.value)
  }
})

watch(video, async (v) => {
  if (v && props.canScaleVideo) {
    await nextTick()
    syncVideoBaseSize()
  }

  if (!v) {
    setDefault()
    // Only clear when stream is gone — transient <video> remounts must not flash loader
    if (!stream.value) {
      isVideoLoaded.value = false
    }
    return
  }

  syncVideoLoadedState()
})

const getPrimaryTrackId = (mediaStream?: MediaStream) =>
  mediaStream?.getVideoTracks()[0]?.id ?? mediaStream?.getAudioTracks()[0]?.id

watch(stream, async (newStream, oldStream) => {
  // Same underlying track (new MediaStream wrapper) — keep loaded state, avoid loader flash
  if (newStream && oldStream && getPrimaryTrackId(newStream) === getPrimaryTrackId(oldStream)) {
    return
  }

  if (newStream?.id === oldStream?.id) {
    return
  }

  if (!newStream) {
    isVideoLoaded.value = false
    videoAspectRatio.value = undefined
    return
  }

  await nextTick()
  syncVideoLoadedState()

  const el = video.value
  if (!el || el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    isVideoLoaded.value = false
  }
})

watch(videoSelectorIsShowed, (v) => {
  if (v) {
    setTimeout(() => {
      videoSelectorIsShowed.value = false
    }, 5000)
  }
})

const isSelected = computed(() => {
  if (props.stream) {
    return props.selectedByStream
  }

  return selectedContactId.value === contact.value.callId
})

const onClick = () => {
  callSessionTileSelect?.setActiveCallId(props.contact.callId, props.stream?.id)
  emit('select', props.contact.callId, props.stream)

  const layoutStore = callSessionsLayoutsStore()
  if (layoutStore.layoutsByRoomPServed.get(props.roomPServed) === Layouts.GRID && isStreamMediaActive.value) {
    setLayout(props.roomPServed, Layouts.LARGE, { isManual: true })
    return
  }

  layoutStore.markLayoutAsManual(props.roomPServed)
}
</script>

<style scoped>
.avatar-card--compact {
  height: 55%;
  max-height: 128px;
  container-type: size;
}

.avatar-card--compact :deep(p) {
  font-size: 40cqh;
  line-height: 1.25;
}
</style>
